import { observable, reaction, runInAction, when } from "mobx"
import { toast } from "sonner"

import { getDomicileBackendHost } from "@/lib/apiClient/client.ts"
import { getLogger } from "@/lib/logging.ts"
import { Model } from "@/lib/Model.ts"
import { clearStoredAuthToken, getStoredAuthToken } from "@/lib/oauth/AuthController.ts"
import { Navigator } from "@/routes/Navigator.ts"

import type { BuildLocationFn } from "@tanstack/react-router"

const MATTER_HOST = import.meta.env.VITE_MATTER_HOST || "http://localhost:8088"

interface UserPrefillData {
  firstName?: string
  lastName?: string
  email?: string
  username?: string
}

interface ResolveTokenResponse {
  valid: boolean
  expired?: boolean
  error?: string
  inviterName?: string
  orgName?: string
  prefillData?: UserPrefillData
  landingPath?: string,
  orgHasProjects?: boolean
}

interface AcceptInvitationResponse {
  success: boolean
  error?: string
  profileId?: string
}

const log = getLogger("InvitationViewModel")
const usernameRegex = /^[A-Za-z0-9]([A-Za-z0-9._-]{0,28}[A-Za-z0-9])?$/

export class InvitationViewModel extends Model {
  readonly submitting = observable.box(false)
  readonly tokenResolved = observable.box(false)
  readonly invitationValid = observable.box(false)
  readonly invitationExpired = observable.box(false)
  readonly errorMessage = observable.box<string | null>(null)
  readonly inviterName = observable.box<string | null>(null)
  readonly orgName = observable.box<string | null>(null)
  readonly firstName = observable.box("")
  readonly lastName = observable.box("")
  readonly username = observable.box("")
  readonly usernameManuallyTouched = observable.box(false)
  readonly landingPath = observable.box<string | null>(null)
  readonly orgHasProjects = observable.box<boolean | null>(null)

  private apiHost: string | null = null

  constructor(private token: string, private orgId: string, private buildLocation: BuildLocationFn) {
    super("InvitationViewModel")

    this.lifetime.add(reaction(
      () => ({
        firstName: this.firstName.get().trim().toLocaleLowerCase(),
        lastName: this.lastName.get().trim().toLocaleLowerCase(),
        manuallyTouched: this.usernameManuallyTouched.get()
      }),
      ({ firstName, lastName, manuallyTouched }) => {
        if (!manuallyTouched && this.username.get().trim().length == 0) {
          if (firstName && lastName) {
            this.username.set(`${firstName}.${lastName}`)
          } else if (firstName) {
            this.username.set(firstName)
          } else if (lastName) {
            this.username.set(lastName)
          } else {
            this.username.set("")
          }
        }
      }
    ), "InvitationViewModel:cleanup")

    this.lifetime.add(reaction(
      () => this.username.get().trim(),
      (username) => {
        if (username.length > 0) {
          this.validateUsername()
        }
      }
    ), "InvitationViewModel:usernameValidation")

    this.resolveToken().catch(error => {
      log.error("Failed to resolve invitation token:", error)
    })
  }

  async awaitReady() {
    await when(() => this.tokenResolved.get())
  }

  validateUsername(): boolean {
    const username = this.username.get().trim()
    if (username.length === 0) {
      return false
    }

    const isValid = usernameRegex.test(username)
    if (!isValid) {
      this.errorMessage.set("Please use only letters, numbers, dots, dashes, or underscores (2-30 characters)")
    } else {
      if (this.errorMessage.get()) {
        this.errorMessage.set(null)
      }
    }
    return isValid
  }

  get canSubmit() {
    return this.firstName.get().trim().length > 0 &&
      this.lastName.get().trim().length > 0 &&
      this.validateUsername() &&
      !this.submitting.get()
  }

  async resolveToken() {
    try {
      this.apiHost = await getDomicileBackendHost(MATTER_HOST, this.orgId)

      const tokenInfo = getStoredAuthToken()
      if (!tokenInfo) {
        throw new Error("No token found in localStorage")
      }

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        "Authorization": tokenInfo.accessToken
      }

      const response = await fetch(`${this.apiHost}/${this.orgId}/api/invitations/resolve-token?token=${encodeURIComponent(this.token)}`, {
        method: "GET",
        headers,
      })

      if (response.status === 401) {
        clearStoredAuthToken()

        const currentUrl = this.buildLocation(Navigator.invite(this.token, this.orgId)).href
        localStorage.setItem("redirect", currentUrl)
        window.location.href = this.buildLocation(Navigator.login()).href
        return
      }

      const result: ResolveTokenResponse = await response.json()

      runInAction(() => {
        this.tokenResolved.set(true)
        this.invitationValid.set(result.valid)
        this.invitationExpired.set(result.expired || false)
        this.errorMessage.set(result.error || null)
        this.inviterName.set(result.inviterName || null)
        this.orgName.set(result.orgName || null)
        this.landingPath.set(result.landingPath || null)
        this.orgHasProjects.set(result.orgHasProjects || null)

        if (result.prefillData) {
          if (result.prefillData.firstName) {
            this.firstName.set(result.prefillData.firstName)
          }
          if (result.prefillData.lastName) {
            this.lastName.set(result.prefillData.lastName)
          }
          if (result.prefillData.username) {
            // Strip everything after @ from username (same as in DarkmatterAuthentication.kt)
            const username = result.prefillData.username.split("@")[0]
            this.username.set(username)
          }
        }
      })
    } catch (error) {
      runInAction(() => {
        this.tokenResolved.set(true)
        this.invitationValid.set(false)
        this.errorMessage.set(error instanceof Error ? error.message : "Failed to resolve invitation")
      })
    }
  }

  async acceptInvitation() {
    if (!this.canSubmit) return

    this.submitting.set(true)

    try {
      const tokenInfo = getStoredAuthToken()
      if (!tokenInfo) {
        throw new Error("No token found in localStorage")
      }

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        "Authorization": tokenInfo.accessToken
      }

      const response = await fetch(`${this.apiHost}/${this.orgId}/api/invitations/accept`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          token: this.token,
          firstName: this.firstName.get().trim(),
          lastName: this.lastName.get().trim(),
          username: this.username.get().trim(),
        }),
      })

      const result: AcceptInvitationResponse = await response.json()

      if (result.success) {
        toast.success("Profile created successfully!")
        const landingPath = this.landingPath.get()
        window.location.href = landingPath
          ? this.buildLocation({ to: `/${this.orgId}/${landingPath.replace(/^\//, "")}` }).href
          : this.orgHasProjects.get()
            ? this.buildLocation(Navigator.orgHome(this.orgId)).href
            : this.buildLocation(Navigator.newProject(this.orgId, { closeLocation: Navigator.orgHome(this.orgId) })).href
      } else {
        runInAction(() => {
          this.errorMessage.set(result.error || "Failed to accept invitation")
          this.submitting.set(false)
        })
      }
    } catch (error) {
      runInAction(() => {
        this.errorMessage.set(error instanceof Error ? error.message : "Failed to accept invitation")
        this.submitting.set(false)
      })
    }
  }
}
